import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, MATERIAL_COLORS } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import {
  Users, ClipboardList, CheckCircle2, Coins, Inbox, UserCheck,
  Phone, Mail, MessageCircle, ChevronRight, Crown,
} from "lucide-react";

function normalizePhoneForWa(raw) {
  if (!raw) return "";
  const d = String(raw).replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("33")) return d;
  if (d.startsWith("0")) return "33" + d.slice(1);
  return d;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("vue");
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, c, l] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/commercials"),
          api.get("/admin/leads"),
        ]);
        setStats(s.data);
        setCommercials(c.data);
        setLeads(l.data);
      } catch (err) {
        if (err?.response?.status === 403) setDenied(true);
      }
    })();
  }, []);

  if (denied) {
    return (
      <div className="text-center py-14">
        <Crown className="w-12 h-12 text-stone-300 mx-auto mb-3" strokeWidth={2} />
        <h1 className="font-heading font-black text-2xl">Accès réservé</h1>
        <p className="text-stone-500 mt-2">Cette page est réservée à l&apos;administrateur de FB Environnement.</p>
        <Link to="/mon-espace" className="mt-5 inline-flex items-center gap-2 bg-stone-900 text-white font-bold px-5 py-3 rounded-full">
          Retour à mon espace
        </Link>
      </div>
    );
  }

  const markLead = async (lead, status) => {
    await api.patch(`/admin/leads/${lead.id}`, { status });
    const { data } = await api.get("/admin/leads");
    setLeads(data);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1F6B2E] to-[#0f3a18] text-white grid place-items-center shadow-lg shadow-[#1F6B2E]/30">
          <Crown className="w-6 h-6" strokeWidth={2.5} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Espace administrateur</p>
          <h1 className="font-heading font-black text-3xl tracking-tight">Vue globale — {user?.name}</h1>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 no-scrollbar">
        {[
          ["vue", "Vue d'ensemble"],
          ["commerciaux", `Commerciaux (${commercials.length})`],
          ["leads", `Demandes (${leads.filter((l) => l.status === "nouveau").length})`],
        ].map(([k, lbl]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`admin-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all active:scale-95 ${
              tab === k ? "bg-stone-900 text-white shadow-lg" : "bg-white border border-stone-200 text-stone-700 hover:bg-stone-50"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {tab === "vue" && stats && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard icon={<UserCheck />} label="Commerciaux" value={stats.commercials_count} color="bg-indigo-500" />
            <StatCard icon={<Users />} label="Clients" value={stats.clients_count} color="bg-sky-500" />
            <StatCard icon={<ClipboardList />} label="Visites" value={stats.visits_count} color="bg-amber-500" />
            <StatCard icon={<CheckCircle2 />} label="Signés" value={stats.signed_count} color="bg-emerald-500" />
            <StatCard icon={<Coins />} label="CA estimé" value={`${(stats.estimated_ca || 0).toLocaleString("fr-FR")} €`} color="bg-stone-900" />
            <StatCard icon={<Inbox />} label="Nouvelles demandes" value={stats.new_leads} color="bg-rose-500" />
          </div>
          {Object.keys(stats.tonnage_by_material || {}).length > 0 && (
            <div className="bg-white rounded-3xl border border-stone-100 p-5">
              <h3 className="font-heading font-black text-lg mb-3">Tonnage cumulé par matière</h3>
              <div className="space-y-2">
                {Object.entries(stats.tonnage_by_material).map(([type, t]) => (
                  <div key={type} className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${MATERIAL_COLORS[type] || "bg-stone-500 text-white"}`}>
                      {type}
                    </span>
                    <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${(MATERIAL_COLORS[type] || "bg-stone-500").split(" ")[0]} rounded-full`}
                        style={{ width: `${Math.min(100, (t / 100) * 100)}%` }}
                      />
                    </div>
                    <span className="font-bold tabular-nums w-20 text-right">{t.toFixed(1)} t</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "commerciaux" && (
        <div className="space-y-3">
          {commercials.length === 0 ? (
            <div className="text-center text-stone-400 py-10">Aucun commercial inscrit.</div>
          ) : (
            commercials.map((c) => (
              <div key={c.id} data-testid={`admin-commercial-${c.id}`} className="bg-white border border-stone-100 rounded-3xl p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-stone-900 text-white grid place-items-center font-heading font-black text-sm shrink-0">
                    {c.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-black text-base">{c.name}</div>
                    <div className="text-xs text-stone-500">{c.email}</div>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                      <Mini label="Clients" value={c.clients_count} />
                      <Mini label="Visites" value={c.visits_count} />
                      <Mini label="Signés" value={c.signed_count} />
                      <Mini label="CA" value={`${(c.estimated_ca || 0).toLocaleString("fr-FR")} €`} highlight />
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "leads" && (
        <div className="space-y-3">
          {leads.length === 0 ? (
            <div className="bg-white border border-dashed border-stone-200 rounded-3xl p-10 text-center text-stone-500">
              Aucune demande pour le moment.
            </div>
          ) : (
            leads.map((l) => {
              const wa = normalizePhoneForWa(l.phone);
              return (
                <div key={l.id} data-testid={`admin-lead-${l.id}`} className={`bg-white border rounded-3xl p-4 ${l.status === "nouveau" ? "border-rose-200 bg-rose-50/30" : "border-stone-100"}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="font-heading font-black text-base">
                        {l.name}
                        {l.company && <span className="text-stone-500 font-bold"> · {l.company}</span>}
                      </div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        {new Date(l.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                        {l.city && ` · ${l.city}`}
                      </div>
                    </div>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${l.status === "nouveau" ? "bg-rose-500 text-white" : l.status === "traite" ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>
                      {l.status === "nouveau" ? "Nouveau" : l.status === "traite" ? "Traité" : l.status}
                    </span>
                  </div>
                  <p className="text-sm text-stone-700 mt-2 whitespace-pre-wrap">{l.message}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {l.phone && (
                      <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs px-3 py-2 rounded-full">
                        <Phone className="w-3.5 h-3.5" strokeWidth={2.8} /> {l.phone}
                      </a>
                    )}
                    {wa && (
                      <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold text-xs px-3 py-2 rounded-full">
                        <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.8} /> WhatsApp
                      </a>
                    )}
                    {l.email && (
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 bg-sky-500 hover:bg-sky-600 text-white font-bold text-xs px-3 py-2 rounded-full">
                        <Mail className="w-3.5 h-3.5" strokeWidth={2.8} /> Email
                      </a>
                    )}
                    {l.status === "nouveau" ? (
                      <button onClick={() => markLead(l, "traite")} data-testid={`mark-treated-${l.id}`} className="ml-auto inline-flex items-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white font-bold text-xs px-3 py-2 rounded-full">
                        Marquer traité <ChevronRight className="w-3.5 h-3.5" strokeWidth={2.8} />
                      </button>
                    ) : (
                      <button onClick={() => markLead(l, "nouveau")} className="ml-auto inline-flex items-center gap-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold text-xs px-3 py-2 rounded-full">
                        Rouvrir
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-4 shadow-sm">
      <div className={`w-9 h-9 rounded-xl ${color} text-white grid place-items-center mb-2 shadow-md`}>
        {React.cloneElement(icon, { className: "w-4 h-4", strokeWidth: 2.5 })}
      </div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500">{label}</div>
      <div className="font-heading font-black text-xl mt-0.5">{value}</div>
    </div>
  );
}

function Mini({ label, value, highlight }) {
  return (
    <div className={`rounded-xl px-3 py-2 ${highlight ? "bg-[#1F6B2E] text-white" : "bg-stone-50"}`}>
      <div className={`text-[10px] uppercase tracking-wider font-bold ${highlight ? "text-[#9DCB2E]" : "text-stone-500"}`}>{label}</div>
      <div className="font-heading font-black text-base">{value}</div>
    </div>
  );
}
